import { useEffect, useRef, useState } from 'react'
import { Check, CircleCheck } from 'lucide-react'
import { useNavigate } from 'react-router'
import { emit } from '../../platform/analytics/analytics'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Icon } from '../../platform/icons/Icon'
import { CategoryField } from '../categories/picker'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { Button } from '../../platform/primitives/Button'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts, type Account } from '../accounts/useAccounts'
import type { MemorySuggestion, Txn } from '../transactions/transactionWire'
import { accountLabel, avatarOf, shortDate } from '../transactions/txnDisplay'
import { useAutosaveField, type SaveStatus } from '../transactions/useAutosaveField'
import { useCategoryMemoryOffer } from '../transactions/useCategoryMemoryOffer'
import { useFilterOptions, type FilterOptions } from '../transactions/useFilterOptions'
import type { TxnPage } from '../transactions/useTransactions'
import { refreshTime, ReviewConflictError, useMarkReviewed, usePendingReview, useReviewSummary } from './useReview'
import './review.css'

export const DONE_FAILED = 'Couldn’t mark these as reviewed. Nothing was changed. Try again in a moment.'
/** Shown when Done finds a row that has gone; the endpoint is all-or-nothing, so nothing was marked. */
export const DONE_CONFLICT =
  'One of these transactions changed since the list loaded. The list has been refreshed — review and try again.'

type Shared = {
  options: FilterOptions
  accounts: ReadonlyMap<string, Pick<Account, 'name' | 'mask'>>
  /** What the server answered after a category change, by id: the row's truth once it has been edited. */
  confirmed: ReadonlyMap<string, Txn>
  onConfirmed: (txn: Txn, index: number) => void
  /** Every answer the server gives, so `confirmed` is the last one even when the latest edit then failed. */
  onAnswer: (txn: Txn) => void
  /** One offer hook for the screen, not one per row: its one-loop-at-a-time guard must span the rows. */
  onMemorySuggestion: (s: MemorySuggestion) => void
}

/**
 * `/review` — the batch review screen (M6-R19 as revised by Q2, iOS #600's model): every pending row
 * in one list, each with a category control that saves in place. There is no stepper and no skip.
 * **Done & Dismiss** marks exactly the rows on screen as reviewed; a refresh that lands meanwhile
 * leaves its new rows pending, and the entry card comes back for them.
 *
 * `live` (Q7): `refreshed_at` is copy ("fetched in the 3:47 pm refresh"), never a stale state.
 */
export function Review() {
  const state = usePendingReview()
  const summary = useReviewSummary()
  const phone = useBreakpoint() === 'phone'
  const navigate = useNavigate()
  const toast = useToast()
  const options = useFilterOptions()
  const accountsState = useAccounts()
  const { run, pending } = useMarkReviewed()
  const [confirmed, setConfirmed] = useState<ReadonlyMap<string, Txn>>(() => new Map())
  const [error, setError] = useState<string | null>(null)
  const [startedAt] = useState(() => Date.now())
  const announced = useRef(false)
  const memory = useCategoryMemoryOffer('review')

  const accounts = new Map(
    accountsState.status === 'success' || accountsState.status === 'stale'
      ? accountsState.data.map((a) => [a.id, a] as const)
      : [],
  )
  const rows = state.status === 'success' ? state.data.rows : undefined

  // Once per visit, on the first render that actually has rows to review.
  useEffect(() => {
    if (rows === undefined || rows.length === 0 || announced.current) return
    announced.current = true
    emit('review_queue_started', { item_count: rows.length })
  }, [rows])

  const view: AsyncState<TxnPage> =
    state.status === 'success' && state.data.rows.length === 0 ? { status: 'empty' } : state
  const refreshedAt = summary.status === 'success' ? summary.data.refreshedAt : null
  const subtitle =
    state.status === 'success' && state.data.rows.length > 0
      ? `${state.data.total.toLocaleString('en-CA')} to review${refreshedAt === null ? '' : ` · fetched in the ${refreshTime(refreshedAt)} refresh`}`
      : null

  const shared: Shared = {
    options,
    onMemorySuggestion: memory.offer,
    accounts,
    confirmed,
    onAnswer: (txn) => setConfirmed((m) => new Map(m).set(txn.id, txn)),
    onConfirmed: (_txn, index) => {
      emit('review_item_categorised', { item_index: index })
    },
  }

  /** The ids rendered right now, and only those: a row the server has since gained stays pending. */
  const done = async (shown: readonly Txn[]) => {
    setError(null)
    const ids = shown.map((t) => t.id)
    try {
      await run(ids, 'reviewed')
    } catch (e) {
      setError(e instanceof ReviewConflictError ? DONE_CONFLICT : DONE_FAILED)
      return
    }
    emit('review_queue_cleared', {
      item_count: ids.length,
      duration_seconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
    })
    toast.show({ kind: 'success', message: `Reviewed ${ids.length.toLocaleString('en-CA')} transaction${ids.length === 1 ? '' : 's'}` })
    navigate('/transactions')
  }

  return (
    <div className="review">
      <header className="review-header">
        <h2>Review</h2>
        {subtitle !== null && <p className="review-header__sub">{subtitle}</p>}
      </header>
      <Panel className="review-panel">
        <Async
          surface="review"
          state={view}
          skeleton={
            <div className="review-skeleton">
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </div>
          }
          empty={{
            title: 'Nothing to review',
            body: 'New bank transactions will appear here after your next sync.',
            icon: (
              <span className="review-empty-icon">
                <Icon glyph={CircleCheck} size={24} />
              </span>
            ),
          }}
          announce={(p) => `${p.rows.length} transactions to review`}
        >
          {(page) => (
            <>
              {phone ? <ReviewCards rows={page.rows} {...shared} /> : <ReviewGrid rows={page.rows} {...shared} />}
              {page.hasMore && (
                <p className="review-more">
                  Showing the first {page.rows.length} of {page.total.toLocaleString('en-CA')}. The rest stay in review.
                </p>
              )}
              <div className={`review-bar${phone ? ' review-bar--phone' : ''}`}>
                {error !== null && (
                  <p className="review-bar__error" role="alert">
                    {error}
                  </p>
                )}
                <Button
                  type="button"
                  size="page"
                  variant="primary"
                  aria-disabled={pending}
                  onClick={() => {
                    // aria-disabled still dispatches clicks; and a native `disabled` would drop focus to the page.
                    if (!pending) void done(page.rows)
                  }}
                >
                  {pending ? 'Saving…' : 'Done & Dismiss'}
                </Button>
              </div>
            </>
          )}
        </Async>
      </Panel>
    </div>
  )
}

/**
 * One row's category control. The autosave engine is the detail screen's, so the failure, ordering and
 * "the response is truth" behaviour are the same. After a real change the server marks the row
 * reviewed (fact 7): it gets a "Reviewed" mark in words and an icon, and stays where it is until the
 * next refresh, so the list does not jump under the user's hand.
 */
function CategoryPick({ txn, index, options, confirmed, onConfirmed, onAnswer, onMemorySuggestion }: { txn: Txn; index: number } & Pick<Shared, 'options' | 'confirmed' | 'onConfirmed' | 'onAnswer' | 'onMemorySuggestion'>) {
  const answer = confirmed.get(txn.id)
  const server = answer === undefined ? txn.categoryId : answer.categoryId
  const nameOf = (id: string | null) =>
    id === null ? 'no category' : (options.categories.flatMap((g) => g.items).find((c) => c.id === id)?.label ?? 'its previous category')
  const category = useAutosaveField<string | null>(txn.id, 'category', server, (v) => ({ category_id: v }), (t) => t.categoryId, {
    describe: nameOf,
    onConfirmed: () => onConfirmed(txn, index),
    onAnswer,
    onMemorySuggestion,
  })
  const reviewed = answer !== undefined && answer.reviewStatus !== 'pending'
  const status: SaveStatus = category.status
  return (
    <span className="review-pick">
      <CategoryField
        className="review-pick__select"
        label={`Category for ${txn.merchant}`}
        value={category.value}
        onChange={category.set}
        allowNone
        context={{ merchant: txn.merchant, date: txn.date, amountCents: txn.amountCents, currency: txn.currency }}
      />
      <span className="review-pick__status" role="status">
        {status === 'saving' ? 'Saving…' : status === 'failed' ? 'Not saved' : null}
        {status !== 'saving' && reviewed && (
          <span className="review-reviewed">
            <Icon glyph={Check} size={14} />
            Reviewed
          </span>
        )}
      </span>
    </span>
  )
}

function ReviewGrid({ rows, ...shared }: { rows: readonly Txn[] } & Shared) {
  const index = new Map(rows.map((t, i) => [t.id, i] as const))
  const columns: Column<Txn>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      width: 'minmax(0, 2fr)',
      render: (t) => (
        <span className="review-merchant">
          <MerchantAvatar merchant={avatarOf(t)} />
          <span className="review-ellipsis">{t.merchant}</span>
        </span>
      ),
    },
    { key: 'date', header: 'Date', width: '80px', render: (t) => shortDate(t.date) },
    {
      key: 'account',
      header: 'Account',
      width: 'minmax(0, 1.3fr)',
      render: (t) => {
        const a = shared.accounts.get(t.accountId)
        return <span className="review-ellipsis">{a ? accountLabel(a) : '—'}</span>
      },
    },
    {
      key: 'category',
      header: 'Category',
      width: 'minmax(0, 2fr)',
      render: (t) => <CategoryPick txn={t} index={index.get(t.id) ?? 0} {...shared} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      width: '110px',
      align: 'end',
      render: (t) => <Money cents={t.amountCents} currency={t.currency} kind="flow" />,
    },
  ]
  return (
    <DataGrid
      label="Transactions to review"
      columns={columns}
      groups={[{ label: '', rows: [...rows] }]}
      rowKey={(t) => t.id}
      empty={null}
    />
  )
}

/** Phone (R28): one card per row, the select full width at the 44px floor. */
function ReviewCards({ rows, ...shared }: { rows: readonly Txn[] } & Shared) {
  return (
    <ul className="review-cards">
      {rows.map((t, i) => {
        const a = shared.accounts.get(t.accountId)
        return (
          <li key={t.id} className="review-card-row">
            <div className="review-card-row__line">
              <span className="review-merchant">
                <MerchantAvatar merchant={avatarOf(t)} />
                <span className="review-ellipsis">{t.merchant}</span>
              </span>
              <Money cents={t.amountCents} currency={t.currency} kind="flow" />
            </div>
            <div className="review-card-row__meta">
              <span>{shortDate(t.date)}</span>
              <span className="review-ellipsis">{a ? accountLabel(a) : '—'}</span>
            </div>
            <CategoryPick txn={t} index={i} {...shared} />
          </li>
        )
      })}
    </ul>
  )
}
